/**
 * Client-side Firestore API for the `payments` collection.
 *
 * All reads and writes are owner-scoped (M0-Konform):
 *   - Every write sets `userId = auth.currentUser.uid`.
 *   - Every read query carries `where('userId', '==', uid)`.
 *
 * Cent-Disziplin: callers must pass `amountCents` as an integer (EUR cents).
 * Use `eurosToCents()` from `utils/moneyUtils.ts` at the UI input boundary.
 *
 * Index requirement (must exist in Firestore before production use):
 *   - `payments`: `userId ASC, invoiceId ASC`  (for getPaymentsForInvoice)
 *   - `payments`: `userId ASC, createdAt DESC` (for getPaymentsForUser)
 *
 * @module paymentsApi
 */

import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { Payment, CreatePaymentInput, PaymentSource, PaymentMethod } from '../types/paymentTypes';
import { AuditService } from '../services/workflow/auditService';
import { getEffectiveScopeUid } from '../utils/tenantScope';
import logger from '../utils/logger';

/**
 * Firestore collection name for payments. Exported (BR-6, §9.3) so `bankApi.ts`
 * writes banking-match payments into the SAME collection without re-declaring
 * the literal (single source of truth — Golden Rule "no copy-paste").
 */
export const PAYMENTS_COLLECTION = 'payments';

/**
 * Shared payment-amount invariant for the regular (incoming/positive) path:
 * `amountCents` must be a positive integer in cents.
 *
 * Exported additively (BR-6, §9.3) so `bankApi.ts`'s `writeBatch` banking-match
 * writes reuse the SAME rule instead of copy-pasting it (Golden Rule "Shared
 * Mapper"). {@link createPayment} deliberately keeps its own inline guard
 * byte-identical (§19 Reviewer checkpoint) — this helper is a new export, not a
 * refactor of the existing guard.
 *
 * @param amountCents - Cent amount to validate.
 * @throws {Error} If not a positive integer.
 */
export function assertPositivePaymentCents(amountCents: number): void {
  if (!Number.isInteger(amountCents) || amountCents <= 0) {
    throw new Error(
      `Payment amountCents must be a positive integer in cents (got ${amountCents}). ` +
      'Use eurosToCents() from moneyUtils.ts to convert user input.'
    );
  }
}

/**
 * Shared storno invariant (BR-6, §8.7/§8.9): a reversal payment carries a
 * NEGATIVE integer amount (it restores the open balance when
 * `computeOpenItemStatus` sums payments sign-aware). Reused by `bankApi.ts`'s
 * unlink / payment-reversal batches. Mirrors the sign expectation of
 * {@link createReversalPayment} (which negates a positive input); here the
 * value is already negated, so the guard requires it to be `< 0`.
 *
 * @param amountCents - Already-negated cent amount to validate.
 * @throws {Error} If not a negative integer.
 */
export function assertStornoPaymentCents(amountCents: number): void {
  if (!Number.isInteger(amountCents) || amountCents >= 0) {
    throw new Error(
      `Storno payment amountCents must be a negative integer in cents (got ${amountCents}).`
    );
  }
}

/**
 * Returns the effective scope UID for owner-scoped queries.
 *
 * Fail-closed: throws if no user is signed in (Golden Rule — never an anonymous
 * scope). The returned value is the *active tenant scope* via
 * {@link getEffectiveScopeUid}: bit-identical to `auth.currentUser.uid` while the
 * flag is off or the own tenant is active (M11 inertness), and the mandate's
 * `scopeUid` when an advisor has a foreign tenant active.
 */
function requireCurrentUser(): string {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('paymentsApi: No authenticated user. Sign in before accessing payments.');
  }
  return getEffectiveScopeUid();
}

/**
 * Writes a best-effort, append-only audit log entry for a payment mutation
 * (M9a GoBD audit-trail extension).
 *
 * Append-only + immutable is guaranteed by the `audit_logs` Firestore Rules
 * (`create: ownsIncoming`, `update,delete: if false`) and a server-authoritative
 * `serverTimestamp()` inside {@link AuditService.addAuditLog}.
 *
 * The audit write runs AFTER the main mutation has already succeeded and is
 * wrapped in its own try/catch: a failed audit write is logged as a warning but
 * never throws, so an audit glitch can never roll back or hide a booked payment
 * (Golden Rule: no unhandled promises, no silent failure).
 *
 * @param uid      - Owner UID (sets `userId` so the `ownsIncoming()` rule passes).
 * @param action   - `'create'` for a new payment, `'cancel'` for a storno reversal.
 * @param targetId - Firestore document ID of the affected payment.
 * @param details  - Additional context merged into the audit `details` object.
 */
async function writePaymentAudit(
  uid: string,
  action: 'create' | 'cancel',
  targetId: string,
  details: Record<string, unknown>,
): Promise<void> {
  try {
    await AuditService.addAuditLog(uid, action, 'payment', targetId, {
      source: 'client',
      ...details,
    });
  } catch (err) {
    logger.warn('paymentsApi: payment audit-log write failed (non-blocking)', err);
  }
}

/**
 * Creates a new payment document in the `payments` collection.
 *
 * Enforces:
 *   - `userId` is always set to `auth.currentUser.uid` (M0 scope, ignores any caller-supplied userId).
 *   - `createdBy` defaults to the current user's UID.
 *   - `createdAt` is set via `serverTimestamp()`.
 *   - `reconciled` defaults to `false` for manual entries.
 *
 * M9a Cent-Discipline: `amountCents` must be a non-zero integer. Negative values
 * are permitted so that a storno/reversal payment can be recorded — the regular
 * correction path is {@link createReversalPayment}, which also sets the
 * `reversalOfPaymentId` back-reference.
 *
 * @param input - Payment fields.  `amountCents` must be a non-zero integer (cents).
 * @returns Firestore document ID of the newly created payment.
 * @throws {Error} If not authenticated or if the Firestore write fails.
 */
export async function createPayment(
  input: Omit<CreatePaymentInput, 'userId' | 'createdBy' | 'source' | 'reconciled'>
    & { invoiceId: string; amountCents: number; source?: PaymentSource; reconciled?: boolean }
): Promise<string> {
  const uid = requireCurrentUser();

  if (!Number.isInteger(input.amountCents) || input.amountCents === 0) {
    throw new Error(
      `createPayment: amountCents must be a non-zero integer (got ${input.amountCents}). ` +
      'Use eurosToCents() from moneyUtils.ts to convert user input.'
    );
  }

  const docData: Omit<Payment, 'id'> = {
    userId: uid,
    invoiceId: input.invoiceId,
    amountCents: input.amountCents,
    paidAt: input.paidAt,
    method: input.method,
    source: input.source ?? 'manual',
    reconciled: input.reconciled ?? false,
    createdBy: uid,
    createdAt: serverTimestamp() as Payment['createdAt'],
  };

  const colRef = collection(db, PAYMENTS_COLLECTION);
  const docRef = await addDoc(colRef, docData);

  await writePaymentAudit(uid, 'create', docRef.id, {
    invoiceId: input.invoiceId,
    amountCents: input.amountCents,
  });

  return docRef.id;
}

/**
 * Records a STORNO (reversal) payment that corrects a mis-recorded payment
 * without ever mutating or deleting the original (M9a GoBD Storno-statt-Löschen).
 *
 * The reversal is a brand-new `payments` document with:
 *   - `amountCents` = the negation of the original positive amount (signed cents),
 *   - `source` = `'storno'`,
 *   - `reversalOfPaymentId` = the original payment's document ID.
 *
 * Because {@link computeOpenItemStatus} sums payment amounts sign-aware, the
 * negative storno restores the invoice's open balance. The original payment
 * stays unchanged and referenced — the Firestore Rules deny any update/delete on
 * `payments`, so reversal is the only correction path.
 *
 * @param input.invoiceId           - Invoice the reversed payment belonged to.
 * @param input.reversalOfPaymentId - Document ID of the original payment to reverse.
 * @param input.amountCents         - The ORIGINAL positive amount in cents; stored negated.
 * @param input.paidAt              - Value date of the reversal (ISO `YYYY-MM-DD` or Timestamp).
 * @param input.method              - Payment method carried over from the original.
 * @returns Firestore document ID of the newly created storno payment.
 * @throws {Error} If not authenticated, the amount is not a positive integer,
 *                 the reference is missing, or the Firestore write fails.
 */
export async function createReversalPayment(
  input: {
    invoiceId: string;
    reversalOfPaymentId: string;
    amountCents: number;
    paidAt: string | Timestamp;
    method: PaymentMethod;
  },
): Promise<string> {
  const uid = requireCurrentUser();

  if (!input.reversalOfPaymentId) {
    throw new Error('createReversalPayment: reversalOfPaymentId is required to reference the original payment.');
  }

  if (!Number.isInteger(input.amountCents) || input.amountCents <= 0) {
    throw new Error(
      `createReversalPayment: amountCents must be the original POSITIVE integer amount to reverse ` +
      `(got ${input.amountCents}); it is stored negated. Use eurosToCents() from moneyUtils.ts.`
    );
  }

  const docData: Omit<Payment, 'id'> = {
    userId: uid,
    invoiceId: input.invoiceId,
    amountCents: -input.amountCents, // negative storno restores the open balance
    paidAt: input.paidAt,
    method: input.method,
    source: 'storno',
    reconciled: false,
    reversalOfPaymentId: input.reversalOfPaymentId,
    createdBy: uid,
    createdAt: serverTimestamp() as Payment['createdAt'],
  };

  const colRef = collection(db, PAYMENTS_COLLECTION);
  const docRef = await addDoc(colRef, docData);

  await writePaymentAudit(uid, 'cancel', docRef.id, {
    reversalOfPaymentId: input.reversalOfPaymentId,
    amountCents: -input.amountCents,
  });

  return docRef.id;
}

/**
 * Loads all payments for a specific invoice, scoped to the current user.
 *
 * Requires a composite Firestore index: `userId ASC, invoiceId ASC`.
 * If the index does not exist, Firestore will return an error with a link to
 * create it in the Firebase console.
 *
 * @param invoiceId - The Firestore document ID of the invoice.
 * @returns Array of Payment objects (may be empty if no payments exist yet).
 * @throws {Error} If not authenticated or if the query fails.
 */
export async function getPaymentsForInvoice(invoiceId: string): Promise<Payment[]> {
  const uid = requireCurrentUser();

  const q = query(
    collection(db, PAYMENTS_COLLECTION),
    where('userId', '==', uid),
    where('invoiceId', '==', invoiceId),
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d): Payment => ({ id: d.id, ...d.data() } as Payment));
}

/**
 * Loads all payments for the current user across all invoices.
 *
 * Useful for the OP-Dashboard overview.
 * Requires a composite Firestore index: `userId ASC, createdAt DESC`.
 *
 * @param uid - UID of the user whose payments to load.
 *              Must match `auth.currentUser.uid` — the Firestore Rules will
 *              reject queries where this does not match.
 * @returns Array of Payment objects ordered by `createdAt` descending.
 * @throws {Error} If not authenticated or if the query fails.
 */
export async function getPaymentsForUser(uid: string): Promise<Payment[]> {
  const currentUid = requireCurrentUser();

  if (uid !== currentUid) {
    throw new Error(
      'getPaymentsForUser: uid must match the authenticated user. ' +
      'Cross-user reads are denied by Firestore Rules.'
    );
  }

  const q = query(
    collection(db, PAYMENTS_COLLECTION),
    where('userId', '==', uid),
    orderBy('createdAt', 'desc'),
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d): Payment => ({ id: d.id, ...d.data() } as Payment));
}

/**
 * @deprecated M9a GoBD-Härtung: payments are immutable from create. The
 * Firestore Rule for `payments` is now `allow update, delete: if false`, so this
 * call will be REJECTED at the server with a permission error. Use
 * {@link createReversalPayment} to correct a mis-recorded payment instead
 * (Storno-statt-Löschen). Kept only for backwards source-compatibility; it has
 * no UI call site (verified by Grep).
 *
 * @param paymentId - Firestore document ID of the payment to delete.
 * @throws {Error} If not authenticated, or — expected under M9a — a Firestore
 *                 permission-denied error because deletes are disallowed.
 */
export async function deletePayment(paymentId: string): Promise<void> {
  requireCurrentUser();

  const docRef = doc(db, PAYMENTS_COLLECTION, paymentId);
  await deleteDoc(docRef);
}
